import { Chain } from '@1delta/chain-registry'
import { Lender } from '@1delta/lender-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import { llamaLendChainData } from '@1delta/data-sdk'
import { LenderAssetReward, RewardStream } from '../types'
import { RewardFetchOutput, RewardFetcher, RewardResult } from './types'

/**
 * Curve LlamaLend gauge rewards.
 *
 * Lives HERE rather than in the LlamaLend public-data fetch on purpose. The
 * lender fetch is already a large multicall per chain and it runs on the hot
 * path for every pool listing; gauge rewards need three more rounds of reads
 * plus an off-chain price call, and none of it is needed to price a market's
 * rate. This is the same split Merkl and the dTRINITY rebate already use — a
 * reward SOURCE, merged in by `fetchLenderRewards`.
 *
 * ## Two mechanisms, not one
 *
 * A LlamaLend lender stakes vault shares in the market's Curve gauge, and the
 * gauge can pay them two different ways:
 *
 *  1. **CRV emissions.** Gauge-controller inflation, boostable with veCRV. The
 *     rate is NOT per-gauge: `inflation_rate` is the GLOBAL CRV rate and every
 *     gauge returns the identical number, so it only becomes this market's
 *     emission after multiplying by `gauge_relative_weight`. Missing that step
 *     reports the whole protocol's emission on every market.
 *  2. **Extra reward tokens.** Streamed in by a distributor — OP on Optimism,
 *     ARB on Arbitrum, and CRV / DOLA / UwU / crvUSD on Ethereum. Unboosted,
 *     and priced against `totalSupply` rather than `working_supply`.
 *
 * ## The traps this file exists to encode
 *
 *  - **`rate` outlives `period_finish`.** Both Optimism OP streams still report
 *    a healthy ~3.7e14 wei/sec while having expired ~11 months ago. An APR
 *    computed as `rate x seconds_per_year` advertises a reward that stopped
 *    last year, which is exactly the failure `RewardStream.endsAt` exists for.
 *  - **Three liveness states, not two.** A gauge can be killed (13 of 27 on
 *    Ethereum), or perfectly alive with `gauge_relative_weight == 0` and
 *    therefore zero CRV. Checking `is_killed` alone still reports emissions on
 *    a zero-weight gauge.
 *  - **The unboosted factor is 0.4.** A depositor with no veCRV earns on 40 %
 *    of their stake, so the headline (1x) APR carries that factor and the
 *    maximum is 2.5x it. Verified against Curve's own UI: this computes 4.71 %
 *    base / 11.78 % boosted where the UI showed 4.81 % / 12.46 %.
 *  - **L1 and L2 gauges differ.** `inflation_rate()` takes no argument on
 *    Ethereum and `inflation_rate(uint256 week)` on the L2 child gauges, where
 *    the no-arg form reverts outright.
 */

const SECONDS_PER_YEAR = 31_536_000
const WAD = 10n ** 18n
const ZERO = '0x0000000000000000000000000000000000000000'

/** veCRV boost bounds. A depositor with no lock earns on 40 % of their stake. */
export const UNBOOSTED_FACTOR = 0.4
export const MAX_BOOST = 2.5

/** Mainnet GaugeController — the only source of a gauge's share of inflation. */
const GAUGE_CONTROLLER = '0x2F50D538606Fa9EDD2B11E2446BEb18C9D5846bB'

/** Chains whose gauges are L1-shaped (no-arg `inflation_rate`, gauge controller). */
const L1_CHAINS = new Set<string>([Chain.ETHEREUM_MAINNET])

const GAUGE_ABI = [
  {
    name: 'inflation_rate',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'working_supply',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'totalSupply',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'is_killed',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'reward_count',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'reward_tokens',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [{ type: 'address' }],
  },
  {
    name: 'reward_data',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [
      { name: 'distributor', type: 'address' },
      { name: 'period_finish', type: 'uint256' },
      { name: 'rate', type: 'uint256' },
      { name: 'last_update', type: 'uint256' },
      { name: 'integral', type: 'uint256' },
    ],
  },
  {
    name: 'gauge_relative_weight',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'decimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint8' }],
  },
  {
    name: 'symbol',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'string' }],
  },
] as const

const scale = (v: bigint, decimals: number) => Number(v) / 10 ** decimals

// ---------------------------------------------------------------------------
// Pure APR math — kept exported so the boost factors and the expiry cliff are
// unit-testable without a chain.
// ---------------------------------------------------------------------------

/**
 * Unboosted CRV APR for a market's gauge, in percent.
 *
 * `inflationRate` is the GLOBAL CRV rate; `relativeWeight` (WAD) is what turns
 * it into this gauge's share. `workingSupply` is the boost-weighted staked
 * balance, which is the correct denominator precisely because the numerator is
 * distributed by working balance.
 */
export function crvGaugeApr(params: {
  inflationRate: bigint
  relativeWeight: bigint
  workingSupply: bigint
  /** Assets per 1e18 shares — LlamaLend shares are ~1000x the asset scale. */
  pricePerShare: bigint
  crvPriceUsd: number
  assetPriceUsd: number
  isKilled: boolean
}): number {
  const {
    inflationRate,
    relativeWeight,
    workingSupply,
    pricePerShare,
    crvPriceUsd,
    assetPriceUsd,
    isKilled,
  } = params
  // A killed gauge and a zero-weight gauge both pay nothing, and neither is
  // implied by the other — a live gauge can hold zero weight.
  if (isKilled) return 0
  if (inflationRate <= 0n || relativeWeight <= 0n || workingSupply <= 0n) {
    return 0
  }
  if (!(crvPriceUsd > 0) || !(assetPriceUsd > 0)) return 0

  const crvPerYear =
    scale(inflationRate, 18) * scale(relativeWeight, 18) * SECONDS_PER_YEAR
  const stakedUsd =
    scale(workingSupply, 18) * scale(pricePerShare, 18) * assetPriceUsd
  if (!(stakedUsd > 0)) return 0

  return ((crvPerYear * crvPriceUsd) / stakedUsd) * 100 * UNBOOSTED_FACTOR
}

/**
 * APR of one extra reward stream, in percent.
 *
 * Denominated against `totalSupply`, not `working_supply`: extra rewards are
 * distributed pro-rata to plain staked balance with no veCRV boost.
 */
export function extraRewardApr(params: {
  rate: bigint
  periodFinish: bigint
  rewardDecimals: number
  rewardPriceUsd: number
  totalSupply: bigint
  pricePerShare: bigint
  assetPriceUsd: number
  nowSeconds: number
}): number {
  const {
    rate,
    periodFinish,
    rewardDecimals,
    rewardPriceUsd,
    totalSupply,
    pricePerShare,
    assetPriceUsd,
    nowSeconds,
  } = params
  // The cliff. `rate` is NOT cleared when a stream ends, so this check is the
  // only thing separating a live program from one that stopped last year.
  if (nowSeconds >= Number(periodFinish)) return 0
  if (rate <= 0n || totalSupply <= 0n) return 0
  if (!(rewardPriceUsd > 0) || !(assetPriceUsd > 0)) return 0

  const rewardPerYearUsd =
    scale(rate, rewardDecimals) * SECONDS_PER_YEAR * rewardPriceUsd
  const stakedUsd =
    scale(totalSupply, 18) * scale(pricePerShare, 18) * assetPriceUsd
  if (!(stakedUsd > 0)) return 0

  return (rewardPerYearUsd / stakedUsd) * 100
}

/**
 * Upper bound for a plausible `period_finish`, in seconds.
 *
 * ~year 4000. Any real timestamp is far below it and an ADDRESS read as a
 * uint256 is far above it (addresses run to ~1.46e48), so the two can never be
 * confused — which is what makes the layout probe below exact rather than a
 * guess.
 */
const MAX_PLAUSIBLE_TIMESTAMP = 64_000_000_000n

/**
 * Decode `reward_data`, which has TWO struct layouts in the wild.
 *
 * Newer gauges return 5 fields starting with `distributor`; older ones return 6
 * starting with `token`. Decoding the 6-field form with a 5-field ABI succeeds
 * silently and shifts every value by one word — the distributor address lands
 * in `period_finish` and reads as ~7e47.
 *
 * That is not a cosmetic error. It produced an `ends_at` of 6.97e47 for a
 * `bigint` column and failed the ENTIRE lending ingest transaction, taking
 * every other lender's rewards down with it. Hence both the layout probe and
 * the sanity gate: a reward source must never be able to break the ingest.
 *
 * Same family of trap as LlamaLend's own controllers, where "v1" is not one
 * ABI — never assume a single shape across a protocol's deployments.
 */
export function decodeRewardData(data: any): {
  periodFinish: bigint
  rate: bigint
} | null {
  if (!data) return null
  const at = (i: number): bigint => {
    try {
      const v = Array.isArray(data) ? data[i] : undefined
      if (typeof v === 'bigint') return v
      if (typeof v === 'string' && v !== '0x') return BigInt(v)
      if (typeof v === 'number') return BigInt(Math.trunc(v))
    } catch {}
    return 0n
  }

  if (!Array.isArray(data)) {
    // Named-tuple decode — viem resolved the fields for us.
    const pf = (data as any).period_finish
    const rt = (data as any).rate
    if (pf === undefined || rt === undefined) return null
    return { periodFinish: BigInt(pf), rate: BigInt(rt) }
  }

  // 5-field layout: [distributor, period_finish, rate, ...]
  let periodFinish = at(1)
  let rate = at(2)

  // An implausible timestamp here means the gauge is really 6-field and every
  // value is one word late: [token, distributor, period_finish, rate, ...].
  if (periodFinish > MAX_PLAUSIBLE_TIMESTAMP) {
    periodFinish = at(2)
    rate = at(3)
  }

  // Still nonsense after the shift — refuse the stream rather than publish a
  // value the database cannot hold.
  if (periodFinish > MAX_PLAUSIBLE_TIMESTAMP) return null
  return { periodFinish, rate }
}

// ---------------------------------------------------------------------------
// Prices
// ---------------------------------------------------------------------------

const LLAMA_CHAIN_SLUG: Record<string, string> = {
  [Chain.ETHEREUM_MAINNET]: 'ethereum',
  [Chain.OP_MAINNET]: 'optimism',
  [Chain.ARBITRUM_ONE]: 'arbitrum',
}

/**
 * USD prices for the reward tokens and the borrowed assets.
 *
 * DefiLlama rather than an on-chain oracle because the tokens involved (CRV,
 * OP, ARB, DOLA, UwU) are not priced by any contract in the LlamaLend system —
 * unlike dTRINITY, which can read its own Aave oracle. One batched call covers
 * every chain.
 */
async function fetchPrices(
  keys: { chainId: string; address: string }[],
): Promise<Record<string, number>> {
  const ids = Array.from(
    new Set(
      keys
        .map(({ chainId, address }) => {
          const slug = LLAMA_CHAIN_SLUG[chainId]
          return slug ? `${slug}:${address.toLowerCase()}` : null
        })
        .filter((x): x is string => !!x),
    ),
  )
  if (ids.length === 0) return {}

  const out: Record<string, number> = {}
  // Chunked: the endpoint takes a path parameter and long lists 414.
  const CHUNK = 60
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK)
    try {
      const res = await fetch(
        `https://coins.llama.fi/prices/current/${slice.join(',')}`,
        { signal: AbortSignal.timeout(10_000) },
      )
      if (!res.ok) continue
      const json: any = await res.json()
      for (const [id, v] of Object.entries<any>(json?.coins ?? {})) {
        if (typeof v?.price === 'number') out[id.toLowerCase()] = v.price
      }
    } catch (e) {
      console.error('[llamalend-gauge] price fetch failed:', e)
    }
  }
  return out
}

const priceOf = (
  prices: Record<string, number>,
  chainId: string,
  address: string,
): number =>
  prices[`${LLAMA_CHAIN_SLUG[chainId]}:${address.toLowerCase()}`] ?? 0

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

interface GaugeMarket {
  controller: string
  vault: string
  gauge: string
  borrowedToken: string
  name?: string
}

function rosterFor(chainId: string): GaugeMarket[] {
  // Degrades to nothing when lender metadata has not been initialised — this
  // fetcher runs standalone and must not throw the whole reward merge.
  const markets = llamaLendChainData(Lender.LLAMALEND, chainId)?.markets ?? []
  return markets
    .filter((m: any) => m.gauge && m.gauge !== ZERO)
    .map((m: any) => ({
      controller: m.controller,
      vault: m.vault,
      gauge: m.gauge,
      borrowedToken: m.borrowedToken,
      name: m.name,
    }))
}

/** `LLAMALEND_<CONTROLLER_HEX_UPPER>` — must match the lending converter. */
const lenderKeyFor = (controller: string) =>
  `${Lender.LLAMALEND}_${controller.replace(/^0x/i, '').toUpperCase()}`

async function fetchForChain(
  chainId: string,
  nowSeconds: number,
): Promise<RewardResult> {
  const markets = rosterFor(chainId)
  if (markets.length === 0) return {}
  const isL1 = L1_CHAINS.has(chainId)
  const week = BigInt(Math.floor(nowSeconds / 604_800))

  // Round 1 — per-gauge state plus the vault's share price.
  const round1 = markets.flatMap((m) => [
    // L2 child gauges take the week; the L1 form takes none and reverts there.
    isL1
      ? { address: m.gauge, name: 'inflation_rate', params: [] }
      : { address: m.gauge, name: 'inflation_rate', params: [week] },
    { address: m.gauge, name: 'working_supply', params: [] },
    { address: m.gauge, name: 'totalSupply', params: [] },
    { address: m.gauge, name: 'is_killed', params: [] },
    { address: m.gauge, name: 'reward_count', params: [] },
    { address: m.vault, name: 'convertToAssets', params: [WAD] },
    isL1
      ? {
          address: GAUGE_CONTROLLER,
          name: 'gauge_relative_weight',
          params: [m.gauge],
        }
      : // No gauge controller off L1; the child gauge's rate is already its own.
        { address: m.gauge, name: 'totalSupply', params: [] },
  ])

  const r1: any[] = (await multicallRetryUniversal({
    chain: chainId,
    calls: round1,
    abi: GAUGE_ABI as any,
    allowFailure: true,
  })) as any[]

  const big = (v: any): bigint => {
    try {
      if (typeof v === 'bigint') return v
      if (typeof v === 'string' && v !== '0x') return BigInt(v)
      if (typeof v === 'number') return BigInt(Math.trunc(v))
    } catch {}
    return 0n
  }

  const STRIDE = 7
  const state = markets.map((m, i) => {
    const b = i * STRIDE
    return {
      market: m,
      inflationRate: big(r1[b]),
      workingSupply: big(r1[b + 1]),
      totalSupply: big(r1[b + 2]),
      isKilled: r1[b + 3] === true,
      rewardCount: Number(big(r1[b + 4])),
      pricePerShare: big(r1[b + 5]),
      // Off L1 the child gauge's own rate is already market-scoped, so the
      // weight is the identity.
      relativeWeight: isL1 ? big(r1[b + 6]) : WAD,
    }
  })

  // Round 2 — the extra reward token addresses.
  const tokenCalls = state.flatMap((s) =>
    Array.from({ length: s.rewardCount }, (_, i) => ({
      address: s.market.gauge,
      name: 'reward_tokens',
      params: [BigInt(i)],
    })),
  )
  const r2: any[] = tokenCalls.length
    ? ((await multicallRetryUniversal({
        chain: chainId,
        calls: tokenCalls,
        abi: GAUGE_ABI as any,
        allowFailure: true,
      })) as any[])
    : []

  let cursor = 0
  const withTokens = state.map((s) => {
    const tokens = r2
      .slice(cursor, cursor + s.rewardCount)
      .filter((t: any) => typeof t === 'string' && t !== ZERO) as string[]
    cursor += s.rewardCount
    return { ...s, tokens }
  })

  // Round 3 — stream data plus each reward token's decimals.
  const dataCalls = withTokens.flatMap((s) =>
    s.tokens.flatMap((t) => [
      { address: s.market.gauge, name: 'reward_data', params: [t] },
      { address: t, name: 'decimals', params: [] },
      { address: t, name: 'symbol', params: [] },
    ]),
  )
  const r3: any[] = dataCalls.length
    ? ((await multicallRetryUniversal({
        chain: chainId,
        calls: dataCalls,
        abi: GAUGE_ABI as any,
        allowFailure: true,
      })) as any[])
    : []

  // Prices: CRV on this chain (only meaningful on L1 today), every reward
  // token, and every borrowed asset.
  const priceKeys: { chainId: string; address: string }[] = []
  for (const s of withTokens) {
    priceKeys.push({ chainId, address: s.market.borrowedToken })
    for (const t of s.tokens) priceKeys.push({ chainId, address: t })
  }
  const CRV_ETH = '0xD533a949740bb3306d119CC777fa900bA034cd52'
  if (isL1) priceKeys.push({ chainId, address: CRV_ETH })
  const prices = await fetchPrices(priceKeys)
  const crvPrice = isL1 ? priceOf(prices, chainId, CRV_ETH) : 0

  const out: RewardResult = {}
  let dCursor = 0

  for (const s of withTokens) {
    const assetPrice = priceOf(prices, chainId, s.market.borrowedToken)
    const streams: RewardStream[] = []

    const crvApr = crvGaugeApr({
      inflationRate: s.inflationRate,
      relativeWeight: s.relativeWeight,
      workingSupply: s.workingSupply,
      pricePerShare: s.pricePerShare,
      crvPriceUsd: crvPrice,
      assetPriceUsd: assetPrice,
      isKilled: s.isKilled,
    })
    if (crvApr > 0) {
      streams.push({
        side: 'deposit',
        apr: crvApr,
        kind: 'token',
        token: {
          address: CRV_ETH.toLowerCase(),
          symbol: 'CRV',
          decimals: 18,
          priceUsd: crvPrice,
        },
        source: {
          id: 'curve:gauge-crv',
          label: 'Curve gauge · CRV',
          platform: 'protocol',
          link: `https://www.curve.finance/lend/ethereum/markets/${s.market.controller}/`,
          refs: {
            gauge: s.market.gauge,
            // The headline is the UNBOOSTED rate; a maxed veCRV lock earns
            // 2.5x it. Published so a consumer can show the range Curve does
            // rather than re-deriving the factor.
            maxBoostApr: crvApr * MAX_BOOST,
          },
        },
        claim: 'accrual',
      })
    }

    for (const token of s.tokens) {
      const data = r3[dCursor]
      const decRaw = r3[dCursor + 1]
      const symRaw = r3[dCursor + 2]
      dCursor += 3
      const decoded = decodeRewardData(data)
      if (!decoded) continue
      const { periodFinish, rate } = decoded
      const decimals =
        typeof decRaw === 'number' ? decRaw : Number(big(decRaw) || 18n)
      const rewardPrice = priceOf(prices, chainId, token)

      const apr = extraRewardApr({
        rate,
        periodFinish,
        rewardDecimals: decimals,
        rewardPriceUsd: rewardPrice,
        totalSupply: s.totalSupply,
        pricePerShare: s.pricePerShare,
        assetPriceUsd: assetPrice,
        nowSeconds,
      })
      if (apr <= 0) continue

      streams.push({
        side: 'deposit',
        apr,
        kind: 'token',
        token: {
          address: token.toLowerCase(),
          symbol: typeof symRaw === 'string' ? symRaw : undefined,
          decimals,
          priceUsd: rewardPrice,
        },
        source: {
          id: 'curve:gauge-extra',
          label: `Curve gauge · ${typeof symRaw === 'string' ? symRaw : 'reward'}`,
          platform: 'protocol',
          link: `https://www.curve.finance/lend/ethereum/markets/${s.market.controller}/`,
          refs: { gauge: s.market.gauge, rewardToken: token },
        },
        claim: 'accrual',
        // The whole point of carrying this: the rate survives expiry. Bounded
        // above by the decoder, so it can never overflow the consumer's column.
        endsAt: Number(periodFinish) || undefined,
      })
    }

    if (streams.length === 0) continue

    const deposit = streams.reduce((sum, st) => sum + st.apr, 0)
    const lenderKey = lenderKeyFor(s.market.controller)
    const asset = s.market.borrowedToken.toLowerCase()
    const reward: LenderAssetReward = {
      distribution: 'onchain-incentives',
      deposit,
      // Gauge rewards pay the LEND side only — the gauge stakes vault shares,
      // and a borrower holds none.
      borrow: 0,
      additionalDepositData: undefined,
      additionalBorrowData: undefined,
      streams,
    } as LenderAssetReward

    if (!out[chainId]) out[chainId] = {}
    if (!out[chainId][lenderKey]) out[chainId][lenderKey] = {}
    out[chainId][lenderKey][asset] = reward
  }

  return out
}

/** Chains with LlamaLend gauges. */
const GAUGE_CHAINS = [
  Chain.ETHEREUM_MAINNET,
  Chain.OP_MAINNET,
  Chain.ARBITRUM_ONE,
]

export const llamaLendGaugeRewardFetcher: RewardFetcher = {
  label: 'LLAMALEND_GAUGE',
  // Lender-keyed: the gauge belongs to exactly one market, so there is no
  // deployment ambiguity to resolve against reserve tokens.
  fetch: async (): Promise<RewardFetchOutput> => {
    const nowSeconds = Math.floor(Date.now() / 1000)
    const results = await Promise.all(
      GAUGE_CHAINS.map(async (chainId) => {
        try {
          return await fetchForChain(chainId, nowSeconds)
        } catch (e) {
          console.error(`[llamalend-gauge] read failed on chain ${chainId}:`, e)
          return {} as RewardResult
        }
      }),
    )
    const byLender: RewardResult = {}
    for (const r of results) {
      for (const [chainId, lenders] of Object.entries(r)) {
        if (!byLender[chainId]) byLender[chainId] = {}
        Object.assign(byLender[chainId], lenders)
      }
    }
    return { byLender }
  },
}
